'use client';

import { c as _c } from "react/compiler-runtime";
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getCuratedContentBadges } from '@src/actions/gamification.actions';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import type { Badge } from '@src/typings/badge';
import { ONE_MINUTE } from '@src/utils/constants.util';
import { instrumentAction } from "@src/utils/instrumentation";
const CONTENT_BADGES_KEY = 'content-badges';
export function invalidateContentBadges(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: [CONTENT_BADGES_KEY]
  });
}
export interface UseContentBadgesArgs {
  slug?: string;
  entityId?: string;
  enabled?: boolean;
}
export function useContentBadges(t0) {
  const $ = _c(34);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    slug,
    entityId,
    enabled
  } = t1;
  const {
    user
  } = useSession();
  const {
    locale
  } = useLocale();
  const guid = user?.guid;
  let t2;
  if ($[2] !== entityId || $[3] !== guid || $[4] !== slug) {
    t2 = [CONTENT_BADGES_KEY, guid, slug, entityId];
    $[2] = entityId;
    $[3] = guid;
    $[4] = slug;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== entityId || $[7] !== guid || $[8] !== locale || $[9] !== slug) {
    t3 = () => instrumentAction("GamificationActions.getCuratedContentBadges", getCuratedContentBadges)({
      locale: locale.code,
      variables: {
        userId: guid,
        entityIds: entityId ? [entityId] : undefined,
        entitySlugs: slug ? [slug] : undefined,
        status: ["active", "available", "completed"]
      }
    });
    $[6] = entityId;
    $[7] = guid;
    $[8] = locale;
    $[9] = slug;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = enabled !== false && !!guid && (!!slug || !!entityId);
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: ONE_MINUTE * 5,
      retry: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false
    };
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    data,
    isLoading,
    refetch
  } = useQuery(t5);
  let t6;
  if ($[15] !== data?.badges) {
    t6 = data?.badges ?? [];
    $[15] = data?.badges;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const badges = t6;
  let t7;
  if ($[17] !== badges || $[18] !== slug) {
    t7 = slug ? badges.filter(b => b.slug === slug && b.status === "active") : badges.filter(_temp);
    $[17] = badges;
    $[18] = slug;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const activeBadges = t7;
  let t8;
  if ($[20] !== badges) {
    t8 = badges.find(_temp2) ?? null;
    $[20] = badges;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const primaryBadge = t8;
  let t9;
  if ($[22] !== badges) {
    t9 = badges.filter(_temp3);
    $[22] = badges;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const completedBadges = t9;
  let t10;
  if ($[24] !== activeBadges || $[25] !== completedBadges || $[26] !== primaryBadge) {
    t10 = {
      activeBadges,
      primaryBadge,
      completedBadges
    };
    $[24] = activeBadges;
    $[25] = completedBadges;
    $[26] = primaryBadge;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const {
    activeBadges: activeBadges_0,
    primaryBadge: primaryBadge_0,
    completedBadges: completedBadges_0
  } = t10;
  let t11;
  if ($[28] !== activeBadges_0 || $[29] !== completedBadges_0 || $[30] !== isLoading || $[31] !== primaryBadge_0 || $[32] !== refetch) {
    t11 = {
      activeBadges: activeBadges_0,
      primaryBadge: primaryBadge_0,
      completedBadges: completedBadges_0,
      areBadgesLoading: isLoading,
      refreshBadges: refetch
    };
    $[28] = activeBadges_0;
    $[29] = completedBadges_0;
    $[30] = isLoading;
    $[31] = primaryBadge_0;
    $[32] = refetch;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
}
function _temp3(b_2) {
  return b_2.status === "completed";
}
function _temp2(b_1) {
  return b_1.primaryBadge && b_1.status !== "completed";
}
function _temp(b_0) {
  return b_0.status === "active";
}